"use client";

// PromptPanel is a generic single-field modal used by mutation
// commands that need one bit of information (a numeric value, a
// driver id, a reason). It deliberately stays minimal: anything
// fancier (multi-field forms, validation, presets) lives in its
// own purpose-built panel component.
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export interface PromptPanelOptions {
  title: string;
  description?: string;
  label: string;
  placeholder?: string;
  type?: "text" | "number" | "textarea";
  defaultValue?: string;
  submitLabel?: string;
  destructive?: boolean;
  // chips render as quick-pick buttons next to the input — used by
  // bump payout (+$5/+$10/+$20), expand radius (5/10/20), etc.
  chips?: { label: string; value: string }[];
}

export function PromptPanel({
  options,
  onSubmit,
  onClose,
}: {
  options: PromptPanelOptions;
  onSubmit: (value: string) => Promise<void> | void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(options.defaultValue ?? "");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await onSubmit(value);
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  const inputId = "prompt-panel-input";
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{options.title}</DialogTitle>
          {options.description ? (
            <DialogDescription>{options.description}</DialogDescription>
          ) : null}
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor={inputId}>{options.label}</Label>
          {options.type === "textarea" ? (
            <Textarea
              id={inputId}
              value={value}
              placeholder={options.placeholder}
              onChange={(e) => setValue(e.target.value)}
              rows={4}
            />
          ) : (
            <Input
              id={inputId}
              type={options.type === "number" ? "number" : "text"}
              value={value}
              placeholder={options.placeholder}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
            />
          )}
          {options.chips && options.chips.length > 0 ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {options.chips.map((chip) => (
                <Button
                  key={chip.value}
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => setValue(chip.value)}
                >
                  {chip.label}
                </Button>
              ))}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant={options.destructive ? "destructive" : "default"}
            onClick={handleSubmit}
            disabled={submitting || value.trim().length === 0}
          >
            {options.submitLabel ?? "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
